import {
  mapFormDataToCarQuoteApiStructure,
  isEmptyObject,
  hasNoQuotes,
  mapFormDataToLifeQuoteApiStructure,
  mapFormDataToFuneralQuoteApiStructure,
  mapFormDataToMedicalGapQuoteApiStructure,
  mapFormDataToMedicalAidQuoteApiStructure,
  mapFormDataToMedicalAidResultsToggleApiStructure,
  mapFormDataToMedicalInsuranceQuoteApiStructure,
  mapFormDataToPetInsuranceQuoteApiStructure,
  mapFormDataToTravelInsuranceQuoteApiStructure,
  mapFormDataToScratchDentCoverQuoteApiStructure,
  mapFormDataToVehicleTrackingQuoteApiStructure,
  mapFormDataToDebtCounsellingOffersApiStructure,
  mapFormDataToBusinessInsuranceQuoteApiStructure,
  mapFormDataToBuildingInsuranceQuoteApiStructure,
  mapFormDataToHomeContentsInsuranceQuoteApiStructure,
  mapFormDataToTyreAndRimInsuranceQuoteApiStructure,
  mapFormDataToMotorWarrantyQuoteApiStructure,
  mapFormDataToServicePlanQuoteApiStructure,
  mapFormDataToEmployeeBenefitCallBackStructure,
  mapFormDataToLoanTier1QuoteApiStructure,
  mapFormAndQuoteDataToLoanTier1QuoteAcceptApiStructure,
  mapFormDataToFibreQuoteApiStructure,
  mapFormAndQuoteDataToFibreQuoteAcceptApiStructure,
  mapFormDataToMotorcycleQuoteApiStructure,
  mapPersonDataToUpdatePersonApiStructure,
  mapVehicleDataToAddVehicleApiStructure,
  mapDriverDataToAddVehicleDriverApiStructure,
  normalizeParkingType,
  mapContactFormDataToApiStructure,
} from '../utils/apiHelpers.js';

import SessionManager from '../utils/sessionManager';
import * as Sentry from '@sentry/react';
import { withExperimentsHeader } from '../experiments/apiHeader';

const BASE_URL = process.env.REACT_APP_HIPPO_API_URL;
const PROVIDER_KEY = process.env.REACT_APP_HIPPO_API_KEY;

function getSessionId() {
  return SessionManager.getSessionId();
}

function getAuthSession() {
  try {
    const raw = localStorage.getItem('authSession');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getAuthHeaders() {
  const session = getAuthSession();
  const token = session?.jwtToken || '';
  if (!token) return {};
  const headers = { Authorization: `Bearer ${token}` };
  if (session?.otpVia === 'email') {
    headers['ValidatedViaEmail'] = '1';
  }
  return headers;
}

// Site is proxied by Cloudflare, so this same-origin endpoint reflects the
// visitor's real IP (equivalent to the origin reading True-Client-IP).
let cachedClientIpPromise = null;
function getClientIPAddress() {
  if (!cachedClientIpPromise) {
    cachedClientIpPromise = fetch('/cdn-cgi/trace')
      .then((res) => res.text())
      .then((text) => text.match(/^ip=(.+)$/m)?.[1] || '')
      .catch(() => '');
  }
  return cachedClientIpPromise;
}

// Attaches the client IP to the request's person data: nested under
// `Person` when present, otherwise at the payload root.
async function withClientIPAddress(payload) {
  const ipAddress = await getClientIPAddress();
  if (payload.Person) {
    payload.Person.IPAddress = ipAddress;
  } else {
    payload.IPAddress = ipAddress;
  }
  return payload;
}

// Attaches the vdn (traffic source identifier) captured from the
// landing URL's query string, or the default vdn if none was captured.
// Nested under `Person` when present, otherwise at the payload root,
// matching withClientIPAddress's placement.
function withVdn(payload) {
  const vdn = SessionManager.getVdn();
  if (payload.Person) {
    payload.Person.VDN = vdn;
  } else {
    payload.VDN = vdn;
  }
  return payload;
}

export async function getEnumMappings() {
  return fetchWithLogging(
    'getEnumMappings',
    `${BASE_URL}/api/Hippo2_0/ViewEnumMappings2_0`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
      },
    }
  );
}

export async function getFeefoDetails() {
  return fetchWithLogging(
    'getFeefoDetails',
    `${BASE_URL}/api/Hippo2_0/GetFeefoDetails2_0`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
      },
    }
  );
}

export async function getPersonPreFill(idNumber) {
  const body = { idNumber, IPAddress: await getClientIPAddress() };
  return fetchWithLogging(
    'getPersonPreFill',
    `${BASE_URL}/api/Hippo2_0/GetPersonPreFill2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    {
      requestBody: body,
      acceptableStatuses: [404],
      timeoutMs: 1500,
    }
  );
}

export async function getVehicleRegPrefill(registrationNumber) {
  const body = { VehicleRegNumber: registrationNumber };
  return fetchWithLogging(
    'getVehicleRegPrefill',
    `${BASE_URL}/api/Hippo2_0/GetVehicleRegPrefill2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    {
      requestBody: body,
      acceptableStatuses: [404],
    }
  );
}

export async function getVehicleSearchString(year, searchString) {
  return fetchWithLogging(
    'getVehicleSearchString',
    `${BASE_URL}/api/Hippo2_0/GetVehicleSearchString2_0/${year}/${searchString}`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
    },
    {
      excludeResponseFromLog: true,
    }
  );
}

export async function getIndustrySearchString(industry) {
  return fetchWithLogging(
    'getBusinessTypes',
    `${BASE_URL}/api/Hippo2_0/GetBusinessTypes2_0/${industry}`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
    },
    {
      excludeResponseFromLog: true,
    }
  );
}

export async function getReturnUserDetails(cellphoneNumber, otp) {
  return fetchWithLogging(
    'getReturnUserDetails',
    `${BASE_URL}/api/Hippo2_0/ReturnUserDetails2_0/${cellphoneNumber}/${otp}`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
    },
    {
      excludeResponseFromLog: true,
      acceptableStatuses: [404, 500], // removed 500 once API fixed
    }
  );
}

export async function postReturnUserSendOtp(cellphoneNumber, firstName) {
  const body = { CellNumber: cellphoneNumber, Firstname: firstName };
  return fetchWithLogging(
    'postReturnUserSendOTP',
    `${BASE_URL}/api/Hippo2_0/ReturnUserSendOTP2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    {
      requestBody: body,
    },
    {
      excludeResponseFromLog: true,
    }
  );
}

export async function postReturnUserSendEmailOtp(email, firstName) {
  const body = { Email: email, Firstname: firstName };
  return fetchWithLogging(
    'postReturnUserSendEmailOTP',
    `${BASE_URL}/api/Hippo2_0/SendOTPViaEmail2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    {
      requestBody: body,
    },
    {
      excludeResponseFromLog: true,
    }
  );
}

export async function getReturnUserDetailsEmail(email, otp) {
  return fetchWithLogging(
    'getReturnUserDetailsEmail',
    `${BASE_URL}/api/Hippo2_0/ReturnUserDetailsEmail2_0/${email}/${otp}`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
    },
    {
      excludeResponseFromLog: true,
      acceptableStatuses: [404, 500], // removed 500 once API fixed
    }
  );
}

export async function getCarQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToCarQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getCarQuote',
    `${BASE_URL}/api/Quote/GetCarQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getFuneralQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToFuneralQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getFuneralQuote',
    `${BASE_URL}/api/Hippo2_0/GetFuneralQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getLifeQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToLifeQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getLifeQuote',
    `${BASE_URL}/api/Hippo2_0/GetLifeQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getMedicalGapQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToMedicalGapQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getMedicalGapQuote',
    `${BASE_URL}/api/Hippo2_0/GetMedicalGapQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getMedicalAidQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToMedicalAidQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getMedicalAidQuote',
    `${BASE_URL}/api/Hippo2_0/GetMedicalAidQuote3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getMedicalAidQuoteResultsToggle(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload =
    mapFormDataToMedicalAidResultsToggleApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getMedicalAidQuoteResultsToggle',
    `${BASE_URL}/api/Hippo2_0/GetMedicalAidQuoteResultsToggle3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getMedicalInsuranceQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToMedicalInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getMedicalInsuranceQuote',
    `${BASE_URL}/api/Hippo2_0/GetMedicalInsuranceQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getPetInsuranceQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToPetInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getPetInsuranceQuote',
    `${BASE_URL}/api/Hippo2_0/GetPetInsuranceQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getTravelInsuranceQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToTravelInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getTravelInsuranceQuote',
    `${BASE_URL}/api/Hippo2_0/GetTravelInsuranceQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getScratchDentCoverQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToScratchDentCoverQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getScratchDentCoverQuote',
    `${BASE_URL}/api/Hippo2_0/GetScratchDentCoverQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getVehicleTrackingQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToVehicleTrackingQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getVehicleTrackingQuote',
    `${BASE_URL}/api/Hippo2_0/GetVehicleTrackingQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getDebtCounsellingOffers(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToDebtCounsellingOffersApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getDebtCounsellingOffers',
    `${BASE_URL}/api/Hippo2_0/GetDebtCounsellingOffers2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getBusinessInsuranceOffers(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload =
    mapFormDataToBusinessInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getVehicleTrackingQuote',
    `${BASE_URL}/api/Hippo2_0/GetBusinessInsuranceOffers2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getBuildingQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload =
    mapFormDataToBuildingInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getBuildingInsuranceQuote',
    `${BASE_URL}/api/Quote/GetBuildingQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getHomeContentsInsuranceQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload =
    mapFormDataToHomeContentsInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getHomeContentsInsuranceQuote',
    `${BASE_URL}/api/Quote/GetHomeContentsQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getTyreAndRimInsuranceQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload =
    mapFormDataToTyreAndRimInsuranceQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getTyreAndRimInsuranceQuote',
    `${BASE_URL}/api/Hippo2_0/GetTyreAndRimQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getMotorWarrantyQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToMotorWarrantyQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getMotorWarrantyQuote',
    `${BASE_URL}/api/Hippo2_0/GetMotorWarrantyQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getServicePlansQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToServicePlanQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getServicePlansQuote',
    `${BASE_URL}/api/Hippo2_0/GetServicePlansQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function requestEmployeeBenefitsContact(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToEmployeeBenefitCallBackStructure(formValues);
  return fetchWithLogging(
    'requestEmployeeBenefitsContact',
    `${BASE_URL}/api/Hippo2_0/RequestEmployeeBenefitsContact2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function acceptQuote(quoteData) {
  return fetchWithLogging(
    'acceptQuote',
    `${BASE_URL}/api/Hippo2_0/AcceptQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(quoteData),
    },
    {
      requestBody: quoteData,
    }
  );
}

// Accepts a quote the user was linked to directly (external landing), where the
// quote and person keys arrive in the URL query string instead of the in-app
// journey. Returns the accepted quote details used to populate the thank you page.
export async function acceptQuoteFromExternal(quoteData) {
  return fetchWithLogging(
    'acceptQuoteFromExternal',
    `${BASE_URL}/api/Hippo2_0/AcceptQuoteFromExternal2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(quoteData),
    },
    {
      requestBody: quoteData,
    }
  );
}

export async function acceptBusinessInsuranceQuote(quoteData) {
  return fetchWithLogging(
    'acceptBusinessInsuranceQuote',
    `${BASE_URL}/api/Hippo2_0/AcceptBusinessInsuranceQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(quoteData),
    },
    {
      requestBody: quoteData,
    }
  );
}

export async function acceptQuoteBuyOnline(quoteData) {
  return fetchWithLogging(
    'acceptQuoteBuyOnline',
    `${BASE_URL}/api/Hippo2_0/AcceptQuoteBuyOnline2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(quoteData),
    },
    {
      requestBody: quoteData,
    }
  );
}

export async function getPersonalLoanT1Offers(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToLoanTier1QuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getPersonalLoanT1Offers',
    `${BASE_URL}/api/Hippo2_0/GetPersonalLoanT1Offers2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function acceptPersonalLoanTier1Quote(quoteData) {
  const apiPayload =
    mapFormAndQuoteDataToLoanTier1QuoteAcceptApiStructure(quoteData);

  return fetchWithLogging(
    'acceptPersonalLoanTier1Quote',
    `${BASE_URL}/api/Hippo2_0/AcceptPersonalLoanTier1Quote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getFibreOffers(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToFibreQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getFibreOffers',
    `${BASE_URL}/api/Hippo2_0/GetFibreOffers2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function acceptFibreQuote(quoteData) {
  const apiPayload =
    mapFormAndQuoteDataToFibreQuoteAcceptApiStructure(quoteData);
  await withClientIPAddress(apiPayload);

  return fetchWithLogging(
    'acceptFibreQuote',
    `${BASE_URL}/api/Hippo2_0/AcceptFibreQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getMotorcycleSearchString(year, searchString) {
  return fetchWithLogging(
    'getMotorcycleSearchString',
    `${BASE_URL}/api/Hippo2_0/GetMotorcycleSearchString2_0/${year}/${searchString}`,
    {
      method: 'GET',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
    },
    {
      excludeResponseFromLog: true,
    }
  );
}

export async function getMotorCycleQuote(formValues) {
  if (isEmptyObject(formValues)) return [];

  const apiPayload = mapFormDataToMotorcycleQuoteApiStructure(formValues);
  await withClientIPAddress(apiPayload);
  withVdn(apiPayload);

  return fetchWithLogging(
    'getMotorCycleQuote',
    `${BASE_URL}/api/Quote/GetMotorCycleQuote2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(apiPayload),
    },
    {
      requestBody: apiPayload,
    }
  );
}

export async function getCrossSellQuotes(identifier) {
  return fetchWithLogging(
    'getCrossSellQuotes',
    `${BASE_URL}/api/Hippo2_0/GetCrossSellQuotes3_0/${identifier}`,
    {
      method: 'POST',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
    },
    { acceptableStatuses: [404] }
  );
}

// --- Profile Endpoints ---

export async function profileSendOTPViaEmail(email, firstName) {
  const body = { Email: email, FirstName: firstName };
  return fetchWithLogging(
    'profileSendOTPViaEmail',
    `${BASE_URL}/api/Hippo2_0/Profile/SendOTPViaEmail2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileSendOTPViaSMS(cellNumber, firstName) {
  const body = { CellNumber: cellNumber, FirstName: firstName };
  return fetchWithLogging(
    'profileSendOTPViaSMS',
    `${BASE_URL}/api/Hippo2_0/Profile/SendOTPViaSMS2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileValidateOTP({ cellNumber, email, otp }) {
  const body = {
    CellNumber: cellNumber || '',
    OTP: otp,
    Emailaddress: email || '',
  };
  return fetchWithLogging(
    'profileValidateOTP',
    `${BASE_URL}/api/Hippo2_0/Profile/ValidateOTP3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileRetrievePerson(identifier) {
  return fetchWithLogging(
    'profileRetrievePerson',
    `${BASE_URL}/api/Hippo2_0/Profile/RetrievePerson3_0/${identifier}`,
    {
      method: 'POST',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
    },
    { acceptableStatuses: [404] }
  );
}

export async function profileUpdatePerson(personData) {
  const body = mapPersonDataToUpdatePersonApiStructure(personData);
  return fetchWithLogging(
    'profileUpdatePerson',
    `${BASE_URL}/api/Hippo2_0/Profile/UpdatePerson3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileRetrieveVehicles(identifier, maxResults = 100) {
  const body = { CellNumber: identifier, MaxResults: maxResults };
  const res = await fetchWithLogging(
    'profileRetrieveVehicles',
    `${BASE_URL}/api/Hippo2_0/Profile/RetrieveVehicles3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
  if (Array.isArray(res?.Vehicles)) {
    res.Vehicles = res.Vehicles.map((v) => {
      if (!v) return v;
      return {
        ...v,
        CarUse: v.CarUse === 'Private' ? 'Personal' : v.CarUse,
        NightTimeSecuritySetup: normalizeParkingType(v.NightTimeSecuritySetup),
        DayTimeSecuritySetup: normalizeParkingType(v.DayTimeSecuritySetup),
      };
    });
  }
  return res;
}

export async function profileAddVehicle(vehicleData) {
  const body = mapVehicleDataToAddVehicleApiStructure(vehicleData);
  return fetchWithLogging(
    'profileAddVehicle',
    `${BASE_URL}/api/Hippo2_0/Profile/AddVehicle3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileDeleteVehicle(vehiclePK) {
  const body = { VehiclePK: vehiclePK };
  return fetchWithLogging(
    'profileDeleteVehicle',
    `${BASE_URL}/api/Hippo2_0/Profile/DeleteVehicle3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileRetrieveLatestQuotes(identifier, maxResults = 2) {
  const body = { CellNumber: identifier, MaxResults: maxResults };
  return fetchWithLogging(
    'profileRetrieveLatestQuotes',
    `${BASE_URL}/api/Hippo2_0/Profile/RetrieveLatestQuotes3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileRetrievePropertyDetails(identifier) {
  return fetchWithLogging(
    'profileRetrievePropertyDetails',
    `${BASE_URL}/api/Hippo2_0/Profile/RetrievePropertyDetails3_0/${identifier}`,
    {
      method: 'POST',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
    },
    { acceptableStatuses: [404] }
  );
}

export async function profileRetrievePetDetails(identifier) {
  return fetchWithLogging(
    'profileRetrievePetDetails',
    `${BASE_URL}/api/Hippo2_0/Profile/RetrievePetDetails3_0/${identifier}`,
    {
      method: 'POST',
      headers: {
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
    },
    { acceptableStatuses: [404] }
  );
}

export async function profileRetrieveVehicleDrivers(
  identifier,
  maxResults = 100
) {
  const body = { CellNumber: identifier, MaxResults: maxResults };
  return fetchWithLogging(
    'profileRetrieveVehicleDrivers',
    `${BASE_URL}/api/Hippo2_0/Profile/RetrieveVehicleDrivers3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileAddVehicleDriver(driverData) {
  const body = mapDriverDataToAddVehicleDriverApiStructure(driverData);
  return fetchWithLogging(
    'profileAddVehicleDriver',
    `${BASE_URL}/api/Hippo2_0/Profile/AddVehicleDriver3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function profileDeleteVehicleDriver(vehicleDriverPK) {
  const body = { VehicleDriverPK: vehicleDriverPK };
  return fetchWithLogging(
    'profileDeleteVehicleDriver',
    `${BASE_URL}/api/Hippo2_0/Profile/DeleteVehicleDriver3_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

export async function postContactUs(formData) {
  const body = mapContactFormDataToApiStructure(formData);
  return fetchWithLogging(
    'postContactUs',
    `${BASE_URL}/api/Hippo2_0/RequestContactUs2_0`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify(body),
    },
    { requestBody: body }
  );
}

// --- Shared Helper Functions ---

// True for the initial quote/offers requests that drive the results "error
// screen" (getCarQuote, getLifeQuote, getBusinessInsuranceOffers, …). Accept,
// cross-sell, fibre-offers and non-quote calls are excluded.
function isQuoteScreenRequest(name) {
  return (
    /^get.*(Quote|Offers)/.test(name) &&
    name !== 'getFibreOffers' &&
    name !== 'getCrossSellQuotes'
  );
}

function stringifyForLog(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// Browsers collapse every opaque network failure — CORS block, DNS failure,
// connection refused, TLS error, offline, blocking extension — into a single
// generic TypeError. The real cause is only ever printed to the browser console
// and is never exposed to JS, so this is as specific as detection can get.
//   Chromium: "Failed to fetch"  Safari: "Load failed"  Firefox: "NetworkError…"
function isOpaqueNetworkError(err) {
  if (!err || err.name !== 'TypeError') return false;
  const m = String(err.message || '').toLowerCase();
  return (
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror')
  );
}

// Coarse failure class for Sentry tagging/filtering.
function classifyRequestError(err) {
  if (err?.name === 'AbortError') return 'timeout';
  if (isOpaqueNetworkError(err)) return 'network'; // CORS/DNS/offline/TLS/blocked
  if (/^HTTP \d{3}$/.test(err?.message || '')) return 'http-status';
  return 'other';
}

// Short, human-readable cause for the error-screen log's Reason field.
function describeRequestError(err) {
  if (!err) return 'Request failed';
  if (err.name === 'AbortError') return 'Request timed out';
  if (isOpaqueNetworkError(err)) {
    return 'Network request failed (CORS, DNS, offline, TLS, or blocked)';
  }
  const label = err.name && err.name !== 'Error' ? `${err.name}: ` : '';
  return `${label}${err.message || 'Request failed'}`;
}

// Fire-and-forget: reports a quote request that failed (network/timeout error,
// non-OK status, or a 200 with no usable quotes) to the error-screen log.
// Deliberately uses a bare fetch (not fetchWithLogging) so it can never recurse,
// and swallows every error so logging never affects the user flow.
function logErrorScreen({ url, requestBody, responseBody, reason = '' }) {
  try {
    fetch(`${BASE_URL}/api/Hippo2_0/LogErrorScreen2_0`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        providerKey: PROVIDER_KEY,
        SessionID: getSessionId(),
      },
      body: JSON.stringify({
        Input: stringifyForLog(requestBody),
        Output: stringifyForLog(responseBody),
        Product: url || '',
        Reason: reason,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let error-screen logging break the flow
  }
}

async function fetchWithLogging(
  name,
  url,
  fetchOptions,
  {
    requestBody = null,
    excludeLogging = false,
    excludeResponseFromLog = false,
    acceptableStatuses = [], // NEW (if not already present)
    timeoutMs, // NEW
  } = {}
) {
  const controller = new AbortController();
  let timerId;
  let responseBody;

  // If caller provided a signal, we can’t merge signals. Prefer caller’s.
  if (!fetchOptions?.signal && typeof timeoutMs === 'number' && timeoutMs > 0) {
    timerId = setTimeout(() => {
      controller.abort(new Error('Timeout'));
    }, timeoutMs);
    fetchOptions = { ...fetchOptions, signal: controller.signal };
  }

  // Optional X-Experiments header. A no-op until SEND_EXPERIMENTS_HEADER is on.
  fetchOptions = withExperimentsHeader(fetchOptions);

  try {
    const response = await fetch(url, fetchOptions);

    responseBody = await response
      .clone()
      .json()
      .catch(() => ({}));

    if (!excludeLogging) {
      logApiCall(name, {
        url,
        method: fetchOptions.method,
        headers: fetchOptions.headers,
        body: requestBody,
        response: excludeResponseFromLog ? undefined : responseBody,
        status: response.status,
      });
    }

    // If a JWT-authenticated request returns 401, the token has expired
    if (
      response.status === 401 &&
      fetchOptions?.headers?.Authorization &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new CustomEvent('hippo:jwt-expired'));
    }

    // allow explicit acceptable statuses (e.g., 404)
    if (!response.ok && !acceptableStatuses.includes(response.status)) {
      throw new Error(`HTTP ${response.status}`);
    }

    // A quote/results request that comes back 200 but with no usable quotes
    // still lands the user on the error screen — log it.
    if (isQuoteScreenRequest(name) && hasNoQuotes(responseBody)) {
      logErrorScreen({
        url,
        requestBody,
        responseBody,
        reason: `No usable quotes (HTTP ${response.status})`,
      });
    }

    return responseBody;
  } catch (err) {
    const reason = describeRequestError(err);

    // A quote/results request that threw (network error, timeout, or non-OK
    // status) also lands the user on the error screen — log it.
    if (isQuoteScreenRequest(name)) {
      logErrorScreen({
        url,
        requestBody,
        responseBody,
        reason,
      });
    }

    // Report real failures to Sentry for alerting. Expected outcomes are
    // already excluded: acceptable statuses (e.g. 404) don't throw, and a 200
    // with no usable quotes returns normally — neither reaches this catch.
    // Full request/response bodies are attached (no anonymisation).
    //
    // The `api_endpoint` tag also marks these as API/backend failures so the
    // replay integration's beforeErrorSampling (see instrument.js) skips them:
    // they alert in Sentry but never pull a session replay.
    const failureType = classifyRequestError(err);

    // Sentry's issue title is `${exception type}: ${exception message}`, and
    // that title (plus the culprit/link) is *all* the Telegram alert bot shows
    // — there's no template to surface tags or the `contexts.api.url` below
    // there. The raw fetch error is generic ("HTTP 404", "Failed to fetch")
    // and identical across every endpoint, so alerts are indistinguishable
    // without clicking through. Prefix the endpoint name + request path into
    // the message so the title alone says what broke and against which route.
    // Stack/name are carried over from the original error so grouping and
    // culprit detection are unaffected.
    let requestPath = url;
    try {
      requestPath = new URL(url).pathname;
    } catch {
      // url wasn't absolute (unexpected) — fall back to the raw value.
    }
    const sentryErr = new Error(`${name} (${requestPath}): ${reason}`);
    sentryErr.name = err?.name || 'Error';
    sentryErr.stack = err?.stack;

    Sentry.captureException(sentryErr, {
      tags: {
        api_endpoint: name,
        // 'network' means an opaque fetch failure — the browser won't tell JS
        // if it was CORS/DNS/offline/TLS/blocked, so filter on this + check
        // network_online to narrow it down.
        failure_type: failureType,
        network_online:
          typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
      },
      contexts: {
        api: {
          endpoint: name,
          url,
          method: fetchOptions?.method,
          reason,
          requestBody,
          responseBody,
          // The title says *which* endpoint failed but not *why* the browser
          // couldn't reach it, so spell out what to check for opaque network
          // failures.
          ...(failureType === 'network' && {
            hint: 'Opaque browser fetch failure — the real cause is not exposed to JS. Likely CORS, DNS, offline, TLS, or a blocking extension. If CORS: check Access-Control-Allow-Origin for this origin on the API (note: calling the prod API from localhost is a common cause).',
          }),
        },
      },
      // Group by endpoint + failure type, so one flaky endpoint is one issue
      // rather than every failure collapsing onto this shared throw site.
      fingerprint: ['api-failure', name, reason],
    });

    // Distinguish timeouts for callers if needed
    if (err?.name === 'AbortError') {
      const e = new Error('FETCH_TIMEOUT');
      e.code = 'FETCH_TIMEOUT';
      e.timeoutMs = timeoutMs;
      throw e;
    }
    throw err;
  } finally {
    if (timerId) clearTimeout(timerId);
  }
}

function logApiCall(name, { url, method, headers, body, response, status }) {
  const newLogEntry = {
    request: { url, method, headers, body },
    status,
  };

  if (response !== undefined) {
    newLogEntry.response = response;
  }
  localStorage.setItem(name, JSON.stringify(newLogEntry));
}
